import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  earn: { key: 'loyalty.typeEarn', fallback: 'Earned' },
  redeem: { key: 'loyalty.typeRedeem', fallback: 'Used on a booking' },
  reversal: { key: 'loyalty.typeReversal', fallback: 'Removed (booking cancelled)' },
  refund: { key: 'loyalty.typeRefund', fallback: 'Returned (booking cancelled)' },
};

export const LoyaltyPanel = ({ sessionToken }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionToken || localStorage.getItem('sessionToken');
    if (!token) {
      setLoading(false);
      return;
    }
    fetch(`${BACKEND_URL}/api/loyalty/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sessionToken]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="loyalty-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('common.loading', 'Loading...')}
      </div>
    );
  }
  if (!data) return null;

  const transactions = data.transactions || [];

  return (
    <div data-testid="loyalty-panel">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-500 p-6 text-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium opacity-90">{t('loyalty.title', 'EnayaHub points')}</p>
            <p className="mt-1 text-3xl font-bold" data-testid="loyalty-points-balance">
              <bdi>{data.points_balance} {t('loyalty.points', 'points')}</bdi>
            </p>
            <p className="mt-1 text-sm opacity-90" data-testid="loyalty-points-value">
              = <bdi>{Number(data.value_kwd).toFixed(3)} {t('common.currency', 'KWD')}</bdi>
            </p>
          </div>
          <Gift className="h-9 w-9 opacity-80" />
        </div>
        <p className="mt-4 text-xs leading-relaxed opacity-90">
          {t('loyalty.explainer', 'Earn 5% back in points on every booking you pay for on EnayaHub — 100 points = 1 KWD. Points are only earned on online bookings, not on cash paid at the shop, and they never expire.')}
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t('loyalty.lifetimeEarned', 'Earned so far')}
          </p>
          <p className="numeric-ltr mt-1 text-lg font-bold text-gray-900">{data.lifetime_earned}</p>
        </div>
        <div className="rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {t('loyalty.lifetimeRedeemed', 'Used so far')}
          </p>
          <p className="numeric-ltr mt-1 text-lg font-bold text-gray-900">{data.lifetime_redeemed}</p>
        </div>
      </div>

      <h4 className="mt-8 mb-3 text-sm font-bold text-gray-900">{t('loyalty.history', 'Points history')}</h4>
      {transactions.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500" data-testid="loyalty-empty">
          {t('loyalty.empty', 'No points yet — book and pay online to start earning.')}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="loyalty-history">
          {transactions.map((tx) => {
            const label = LABELS[tx.type] || { key: 'loyalty.typeEarn', fallback: tx.type };
            const positive = tx.points > 0;
            return (
              <li key={tx.id} className="flex items-center justify-between gap-4 py-3" data-testid="loyalty-history-row">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{t(label.key, label.fallback)}</p>
                  <p className="numeric-ltr text-xs text-gray-500">
                    {new Date(tx.created_at).toLocaleDateString(i18n.language === 'ar' ? 'ar-KW' : 'en-GB')}
                  </p>
                </div>
                <span className={`numeric-ltr text-sm font-bold ${positive ? 'text-emerald-600' : 'text-gray-500'}`}>
                  {positive ? '+' : '−'}{Math.abs(tx.points)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LoyaltyPanel;
